import { useState } from "react";
import {
  syncKodeshboardNow,
  type KodeshboardCounts,
  type KodeshboardSyncResult,
} from "../actions";

/**
 * Pulls yahrzeits from KodeshBoard into the CRM.
 *
 * KodeshBoard owns them — it has the approval workflow, the public form and the
 * board on the synagogue wall. What the CRM adds is the thing a display cannot
 * do: notice that a family should be called the week before. So this is a
 * one-way, read-only pull, and the copy says so, because "sync" usually implies
 * two directions and here it must not.
 *
 * The dry run is offered first and deliberately given equal weight. A first pass
 * over real memorial records should be inspectable before it writes anything,
 * and the skip reasons are the interesting part — they say which records
 * KodeshBoard holds that the CRM cannot use, and why.
 */

const REASON_TEXT: Record<string, string> = {
  pending_approval: "ממתינות לאישור הגבאי ב-KodeshBoard",
  inactive: "מוסתרות מהלוח",
  no_name: "בלי שם",
  unparseable_hebrew_date: "לא הצלחנו לקרוא את התאריך העברי",
  no_contact_phone: "בלי טלפון של איש קשר מהמשפחה",
  no_phone: "בלי טלפון",
  unusable_phone: "מספר טלפון לא תקין",
  contact_is_not_celebrant: "שם איש הקשר שונה מהחוגג. מחכה להחלטה שלכם",
};

/** One block of counts, shown per record type. */
function Counts({
  title,
  c,
  dryRun,
}: {
  title: string;
  c: KodeshboardCounts;
  dryRun: boolean;
}) {
  const skips = Object.entries(c.skippedReasons ?? {});

  return (
    <div className="kb-block">
      <p className="kb-block-title">{title}</p>
      <div className="conn-meta" style={{ marginTop: 0, paddingTop: 0, border: 0 }}>
        <div>
          <span className="faint">נבדקו</span>
          <strong>{c.scanned}</strong>
        </div>
        <div>
          <span className="faint">{dryRun ? "ייכנסו" : "נכנסו"}</span>
          <strong>{c.imported}</strong>
        </div>
        {!dryRun && (
          <>
            <div>
              <span className="faint">אנשי קשר חדשים</span>
              <strong>{c.createdPerson}</strong>
            </div>
            <div>
              <span className="faint">קושרו לקיימים</span>
              <strong>{c.matchedExistingPerson}</strong>
            </div>
          </>
        )}
      </div>

      {skips.length > 0 && (
        <ul className="kb-skips" style={{ marginTop: 8 }}>
          {skips.map(([reason, n]) => (
            <li key={reason}>
              <strong>{n}</strong> — {REASON_TEXT[reason] ?? reason}
            </li>
          ))}
        </ul>
      )}

      {c.ambiguous > 0 && (
        <p className="form-error" style={{ margin: "8px 0 0" }}>
          {c.ambiguous} עם טלפון ומייל ששייכים לשני אנשי קשר שונים. לא איחדנו אותם.
        </p>
      )}
    </div>
  );
}

export function KodeshboardCard() {
  const [busy, setBusy] = useState<null | "dry" | "real">(null);
  const [result, setResult] = useState<KodeshboardSyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(dryRun: boolean) {
    setBusy(dryRun ? "dry" : "real");
    setError(null);
    setResult(null);
    try {
      setResult(await syncKodeshboardNow(dryRun));
    } catch (err) {
      const message = (err as { message?: string })?.message;
      setError(message && /[֐-׿]/.test(message) ? message : "לא הצלחנו למשוך את הנתונים. נסו שוב.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="section">
      <div className="card">
        <div className="card-title">
          אזכרות מ-KodeshBoard
          <span className="conn-pill conn-off">קריאה בלבד</span>
        </div>

        <div className="card-pad">
          <p className="muted">
            פעם ביום אנחנו מושכים את היארצייטים מלוח בית הכנסת, ומקשרים כל אחד לאיש הקשר במשפחה.{" "}
            <strong>הנתונים נשארים של הלוח.</strong> אנחנו רק קוראים ממנו, ומוסיפים את מה שהוא לא יודע לעשות:
            משימה להתקשר לפני התאריך.
          </p>
          <p className="faint" style={{ marginTop: 8 }}>
            אנחנו לא כותבים ולא מוחקים כלום ב-KodeshBoard. וזו לא רק הבטחה: ההרשאה עצמה היא לקריאה בלבד.
          </p>

          {error && <p className="form-error">{error}</p>}

          {result && (
            <div className="kb-result">
              <p className="kb-result-head">
                {result.dryRun ? "בדיקה בלבד. לא שמרנו כלום." : "סיימנו למשוך את הנתונים."}
              </p>
              <Counts title="אזכרות" c={result.memorials} dryRun={result.dryRun} />
              <Counts title="ימי הולדת" c={result.birthdays} dryRun={result.dryRun} />
            </div>
          )}

          <div className="btn-row" style={{ marginTop: 12 }}>
            <button className="btn" onClick={() => run(true)} disabled={busy !== null}>
              {busy === "dry" ? "בודקים…" : "בדיקה בלבד"}
            </button>
            <button className="btn btn-primary" onClick={() => run(false)} disabled={busy !== null}>
              {busy === "real" ? "מושכים…" : "משיכה עכשיו"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
